/**
 * Vorschau mit echten Empfängern.
 *
 * Die Editorvorschau zeigt sonst einen erfundenen „Mag. Mustermann" — und der
 * hat immer alle Felder gefüllt. Genau die Fälle, die im Alltag Ärger machen,
 * sieht man daran nie: der Kontakt ohne Nachnamen, die Firma ohne
 * Ansprechpartner, der Doppelname, das leere Zusatzfeld mitten im Satz.
 *
 * Deshalb liefert der Server bewusst eine MISCHUNG statt der ersten fünf
 * Kontakte, und hier blättert man durch. Wer das einmal vor der Aussendung
 * tut, findet die Lücke — statt sie zweitausendfach zu verschicken.
 */

import { Vorlagen } from './api.js';
import { el, ersetzen, laedt, meldung } from './ui.js';

/**
 * @typedef {object} Steuerung
 * @property {HTMLElement} element
 * @property {() => void} neuZeichnen  Nach einer Änderung an der Vorlage aufrufen.
 */

/**
 * @param {object} optionen
 * @param {() => string} optionen.mjmlLesen  Liefert das aktuelle MJML.
 * @param {() => string} optionen.betreffLesen
 * @returns {Steuerung}
 */
export function empfaengerVorschau({ mjmlLesen, betreffLesen }) {
  const element = el('div.empfaenger-vorschau');
  const kopf = el('div.vorschau-kopf');
  const rahmen = el('div.vorschau-inhalt');

  /** @type {any[]} */
  let kontakte = [];
  let index = 0;
  let geladen = false;

  /**
   * Beschreibt, WARUM dieser Kontakt in der Auswahl ist. Ohne diese Zeile
   * blättert man durch Namen, ohne zu erkennen, worauf zu achten wäre.
   *
   * @param {any} kontakt
   * @returns {string}
   */
  function besonderheit(kontakt) {
    // Reihenfolge nach Aussagekraft: „Firmenkontakt" erklärt die neutrale
    // Anrede, „ohne Nachnamen" wäre bei einer Firma zwar auch wahr, aber
    // irreführend — es klänge nach einer Datenlücke statt nach Absicht.
    if (kontakt.anredeForm === 'firma') return 'Firmenkontakt — bekommt die neutrale Anrede';
    if (!kontakt.nachname) return 'ohne Nachnamen — bekommt die neutrale Anrede';
    if (!kontakt.anredeForm || kontakt.anredeForm === 'neutral') {
      return 'ohne Anredeangabe — bekommt die neutrale Anrede';
    }
    if (kontakt.titelVor) return 'mit akademischem Titel';
    return 'vollständig gepflegt';
  }

  async function zeichnen() {
    if (!kontakte.length) {
      ersetzen(
        element,
        meldung(
          'info',
          geladen
            ? 'Es sind noch keine Empfänger vorhanden. Die Vorschau zeigt deshalb einen Musterempfänger.'
            : 'Empfänger werden geladen …',
        ),
      );
      return;
    }

    const kontakt = kontakte[index];
    ersetzen(
      kopf,
      el('div', [
        el('span.haupttext', {
          text: kontakt.email,
        }),
        el('span.nebentext', { text: besonderheit(kontakt) }),
      ]),
      el('div.blaettern', [
        el('button', {
          type: 'button',
          text: '‹',
          'aria-label': 'Vorheriger Empfänger',
          disabled: kontakte.length < 2,
          onclick: () => {
            index = (index - 1 + kontakte.length) % kontakte.length;
            zeichnen();
          },
        }),
        el('span.zaehler', { text: `${index + 1} / ${kontakte.length}` }),
        el('button', {
          type: 'button',
          text: '›',
          'aria-label': 'Nächster Empfänger',
          disabled: kontakte.length < 2,
          onclick: () => {
            index = (index + 1) % kontakte.length;
            zeichnen();
          },
        }),
      ]),
    );

    ersetzen(rahmen, laedt('Vorschau wird gebaut …'));
    ersetzen(element, kopf, rahmen);

    try {
      const ergebnis = await Vorlagen.vorschau({
        mjml: mjmlLesen(),
        betreff: betreffLesen() || undefined,
        musterKontakt: {
          email: kontakt.email,
          ...(kontakt.anredeForm ? { anredeForm: kontakt.anredeForm } : {}),
          ...(kontakt.vorname ? { vorname: kontakt.vorname } : {}),
          ...(kontakt.nachname ? { nachname: kontakt.nachname } : {}),
          ...(kontakt.titelVor ? { titelVor: kontakt.titelVor } : {}),
          ...(kontakt.firma ? { firma: kontakt.firma } : {}),
        },
      });

      const anzeige = el('div');
      // Die Anrede eigens hervorheben: sie ist der Grund, warum man
      // überhaupt durchblättert.
      anzeige.append(
        el('p.anrede-zeile', { text: `Anrede: ${ergebnis.anrede}` }),
        rahmenMit(ergebnis.html),
      );
      ersetzen(rahmen, anzeige);
    } catch (fehler) {
      ersetzen(
        rahmen,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Vorschau fehlgeschlagen'),
      );
    }
  }

  Vorlagen.vorschauKontakte()
    .then((ergebnis) => {
      kontakte = ergebnis.kontakte ?? [];
      geladen = true;
      zeichnen();
    })
    .catch(() => {
      geladen = true;
      zeichnen();
    });

  ersetzen(element, laedt('Empfänger werden geladen …'));

  return { element, neuZeichnen: () => zeichnen() };
}

/**
 * Zeigt fertiges HTML in einem abgeschotteten Rahmen.
 *
 * `sandbox` ohne `allow-scripts`: die Vorschau soll nur aussehen wie eine
 * E-Mail, nicht sich wie eine Webseite verhalten dürfen.
 *
 * @param {string} html
 * @returns {HTMLElement}
 */
function rahmenMit(html) {
  const rahmen = /** @type {HTMLIFrameElement} */ (
    el('iframe.vorschau-rahmen', {
      sandbox: 'allow-same-origin',
      title: 'Vorschau der Nachricht',
    })
  );
  rahmen.srcdoc = html;
  return rahmen;
}
